import React from 'react';
import { MapPin, Trash2, AlertCircle } from 'lucide-react';
import { Country, State } from 'country-state-city';
import { Address } from '@/types';
import './AddressForm.css';

interface AddressFormProps {
  address: Address;
  index: number | 'primary';
  readOnly?: boolean;
  viewMode?: boolean; // Deprecated, use readOnly instead
  validationErrors: Record<string, string>;
  onUpdate: (index: number | 'primary', field: keyof Address, value: string) => void;
  onRemove: (index: number | 'primary') => void;
  onClearError: (errorKey: string) => void;
}

const AddressForm: React.FC<AddressFormProps> = ({
  address,
  index,
  readOnly,
  viewMode, // Deprecated
  validationErrors,
  onUpdate,
  onRemove,
  onClearError,
}) => {
  // Support both readOnly and deprecated viewMode prop
  const isReadOnly = readOnly ?? viewMode ?? false;
  const errorPrefix = index === 'primary' ? 'address_primary' : `address_${index}`;
  const addressNumber = index === 'primary' ? 1 : (index as number) + 2;
  const addressTitle = index === 'primary' ? 'Primary Address' : `Address ${addressNumber}`;

  const handleFieldChange = (field: keyof Address, value: string) => {
    onUpdate(index, field, value);
    const errorKey = `${errorPrefix}_${field}`;
    if (validationErrors[errorKey]) {
      onClearError(errorKey);
    }

    // Clear dependent errors when country changes
    if (field === 'country') {
      onClearError(`${errorPrefix}_state`);
      onClearError(`${errorPrefix}_postalCode`);
      // Clear state value when country changes
      if (address.state) {
        onUpdate(index, 'state', '');
      }
    }
  };

  return (
    <div className="address-form">
      <div className="address-header">
        <div className="address-header-content">
          <MapPin size={18} className="address-icon" />
          <h3>{addressTitle}</h3>
        </div>
        <div className="address-header-actions">
          <select
            className={`select address-type-select ${isReadOnly ? 'readonly-field' : ''}`}
            value={address.type}
            onChange={(e) => handleFieldChange('type', e.target.value)}
            disabled={isReadOnly}
          >
            <option value="WORK">Work</option>
            <option value="HOME">Home</option>
          </select>
          {!isReadOnly && (
            <button onClick={() => onRemove(index)} className="btn-remove" title="Remove address">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="form-group">
        <label className="label">
          Street Address
          {validationErrors[`${errorPrefix}_street`] && (
            <span className="validation-icon error" title={validationErrors[`${errorPrefix}_street`]}>
              <AlertCircle size={14} />
            </span>
          )}
        </label>
        <div className="input-wrapper">
          <input
            type="text"
            className={`input ${isReadOnly ? 'readonly-field' : ''} ${validationErrors[`${errorPrefix}_street`] ? 'input-error' : ''}`}
            value={address.street || ''}
            onChange={(e) => handleFieldChange('street', e.target.value)}
            placeholder="123 Main Street, Suite 100"
            maxLength={200}
            readOnly={isReadOnly}
          />
          {!isReadOnly && (address.street || '').length > 0 && (
            <small className="character-counter">{(address.street || '').length} / 200</small>
          )}
          {validationErrors[`${errorPrefix}_street`] && (
            <small className="error-text">{validationErrors[`${errorPrefix}_street`]}</small>
          )}
        </div>
      </div>

      <div className="address-row">
        <div className="form-group">
          <label className="label">
            City / District
            {validationErrors[`${errorPrefix}_city`] && (
              <span className="validation-icon error" title={validationErrors[`${errorPrefix}_city`]}>
                <AlertCircle size={14} />
              </span>
            )}
          </label>
          <div className="input-wrapper">
            <input
              type="text"
              className={`input ${isReadOnly ? 'readonly-field' : ''} ${validationErrors[`${errorPrefix}_city`] ? 'input-error' : ''}`}
              value={address.city || ''}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              placeholder="New York"
              maxLength={100}
              readOnly={isReadOnly}
            />
            {validationErrors[`${errorPrefix}_city`] && (
              <small className="error-text">{validationErrors[`${errorPrefix}_city`]}</small>
            )}
          </div>
        </div>
        <div className="form-group">
          <label className="label">
            State/Province
            {validationErrors[`${errorPrefix}_state`] && (
              <span className="validation-icon error" title={validationErrors[`${errorPrefix}_state`]}>
                <AlertCircle size={14} />
              </span>
            )}
          </label>
          <div className="input-wrapper">
            <select
              className={`select ${isReadOnly ? 'readonly-field' : ''} ${validationErrors[`${errorPrefix}_state`] ? 'input-error' : ''}`}
              value={address.state || ''}
              onChange={(e) => handleFieldChange('state', e.target.value)}
              disabled={isReadOnly || !address.country}
            >
              <option value="">Select State/Province</option>
              {address.country && (() => {
                const countries = Country.getAllCountries();
                const foundCountry = countries.find(c => 
                  c.name === address.country || c.isoCode === address.country
                );
                if (foundCountry) {
                  const states = State.getStatesOfCountry(foundCountry.isoCode);
                  return states.map(state => (
                    <option key={state.isoCode} value={state.name}>
                      {state.name}
                    </option>
                  ));
                }
                return null;
              })()}
            </select>
            {validationErrors[`${errorPrefix}_state`] && (
              <small className="error-text">{validationErrors[`${errorPrefix}_state`]}</small>
            )}
          </div>
        </div>
      </div>

      <div className="address-row">
        <div className="form-group">
          <label className="label">
            Postal Code (optional)
            {validationErrors[`${errorPrefix}_postalCode`] && (
              <span className="validation-icon error" title={validationErrors[`${errorPrefix}_postalCode`]}>
                <AlertCircle size={14} />
              </span>
            )}
          </label>
          <div className="input-wrapper">
            <input
              type="text"
              className={`input ${isReadOnly ? 'readonly-field' : ''} ${validationErrors[`${errorPrefix}_postalCode`] ? 'input-error' : ''}`}
              value={address.postalCode || ''}
              onChange={(e) => handleFieldChange('postalCode', e.target.value)}
              placeholder="10001"
              maxLength={20}
              readOnly={isReadOnly}
            />
            {validationErrors[`${errorPrefix}_postalCode`] && (
              <small className="error-text">{validationErrors[`${errorPrefix}_postalCode`]}</small>
            )}
          </div>
        </div>
        <div className="form-group">
          <label className="label">
            Country
            {validationErrors[`${errorPrefix}_country`] && (
              <span className="validation-icon error" title={validationErrors[`${errorPrefix}_country`]}>
                <AlertCircle size={14} />
              </span>
            )}
          </label>
          <div className="input-wrapper">
            <select
              className={`select ${isReadOnly ? 'readonly-field' : ''} ${validationErrors[`${errorPrefix}_country`] ? 'input-error' : ''}`}
              value={address.country || ''}
              onChange={(e) => handleFieldChange('country', e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select Country</option>
              {Country.getAllCountries()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(country => (
                  <option key={country.isoCode} value={country.name}>
                    {country.name}
                  </option>
                ))}
            </select>
            {validationErrors[`${errorPrefix}_country`] && (
              <small className="error-text">{validationErrors[`${errorPrefix}_country`]}</small>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressForm;
